// Scrollbars are invisible at rest and appear only while the user is scrolling
// (issue #38). No engine exposes a "is scrolling" state to CSS — `:hover` is not
// the same thing, since it reveals the bar whenever the pointer happens to rest
// over a list — so the state is kept in one class on <html> and the styling in
// `assets/tailwind.css` keys off it.
//
// One capturing listener on the document catches scrolls on every surface,
// including ones added later, so nothing has to opt in.

/** How long after the last scroll event the bars stay visible. */
export const IDLE_MS = 700

/** The class `assets/tailwind.css` keys the visible state off. */
export const SCROLLING_CLASS = 'scrolling'

/**
 * Starts reflecting scroll activity onto the document element.
 *
 * @param {Document} doc
 * @param {{ idleMs?: number }} [options]
 * @returns {() => void} stop — removes the listener and clears the state.
 */
export function trackScrolling(doc = document, { idleMs = IDLE_MS } = {}) {
  const root = doc.documentElement
  let timer = null

  const onScroll = () => {
    root.classList.add(SCROLLING_CLASS)
    if (timer) clearTimeout(timer)
    // Idle is measured from the LAST scroll event, so a long flick or a slow
    // drag keeps the bar up rather than blinking mid-gesture.
    timer = setTimeout(() => {
      root.classList.remove(SCROLLING_CLASS)
      timer = null
    }, idleMs)
  }

  // Capturing: scroll does not bubble, so a listener on the document only sees
  // scrolls from nested elements if it captures.
  doc.addEventListener('scroll', onScroll, { capture: true, passive: true })

  return () => {
    doc.removeEventListener('scroll', onScroll, { capture: true })
    if (timer) clearTimeout(timer)
    root.classList.remove(SCROLLING_CLASS)
  }
}
